export function buildLyricItems(raw, timedLyrics) {
  if (timedLyrics.length) {
    return timedLyrics.map((item, lyricIndex) => ({
      type: 'lyric',
      text: item.text,
      lyricIndex,
    }));
  }
  const sections = parseLyrics(raw);
  const { title, normalized } = normalizeSections(sections);
  const items = [];
  if (title) items.push({ type: 'title', text: title }, { type: 'spacer' });
  normalized.forEach((section, index) => {
    if (index > 0) items.push({ type: 'spacer' });
    section.lines.forEach((line) => items.push({ type: 'lyric', text: line }));
  });
  return withLyricIndexes(items);
}

export function lyricSyncUnits(items) {
  const units = [];
  let currentLyricIndex = 0;
  items.forEach((item) => {
    if (item.type === 'lyric') {
      currentLyricIndex = item.lyricIndex;
      units.push({ lyricIndex: item.lyricIndex, weight: lyricWeight(item.text) });
    }
    if (item.type === 'spacer' && units.length) {
      units.push({ lyricIndex: currentLyricIndex, weight: 0.65 });
    }
  });
  return units.length ? units : [{ lyricIndex: 0, weight: 1 }];
}

export function getActiveIndex(timedLyrics, units, current, duration) {
  if (timedLyrics.length) return getTimedActiveIndex(timedLyrics, current);
  if (!units.length || !duration) return 0;
  const totalWeight = units.reduce((sum, unit) => sum + unit.weight, 0);
  const position = Math.min(0.999, Math.max(0, current / duration)) * totalWeight;
  let cursor = 0;
  for (const unit of units) {
    cursor += unit.weight;
    if (position <= cursor) return unit.lyricIndex;
  }
  return units[units.length - 1].lyricIndex;
}

export function normalizeTimedLyrics(value) {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => ({
      text: String(item?.text || '').trim(),
      startTimeMs: Number(item?.startTimeMs),
      endTimeMs: Number(item?.endTimeMs),
    }))
    .filter((item) => (
      item.text &&
      Number.isFinite(item.startTimeMs) &&
      Number.isFinite(item.endTimeMs) &&
      item.endTimeMs >= item.startTimeMs
    ))
    .sort((a, b) => a.startTimeMs - b.startTimeMs);
}

export function formatTime(value) {
  const total = Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function parseLyrics(raw) {
  const lines = String(raw || '').split('\n').map((line) => line.trim());
  const sections = [];
  let current = { title: 'Verse', lines: [] };
  lines.forEach((line) => {
    if (!line) return;
    const title = extractSectionTitle(line);
    if (title) {
      if (current.lines.length) sections.push(current);
      current = { title, lines: [] };
      return;
    }
    current.lines.push(line);
  });
  if (current.lines.length) sections.push(current);
  return sections;
}

function extractSectionTitle(text) {
  let body = text.trim();
  const wrapped = body.match(/^[[(（【]\s*(.*?)\s*[\])）】]\s*[:：]?$/);
  body = wrapped ? wrapped[1].trim() : body.replace(/\s*[:：]$/, '');
  const match = body.match(/^(pre[-\s]?chorus|post[-\s]?chorus|verse|chorus|bridge|outro|intro|hook|refrain|interlude|rap|prelude|主歌|副歌|桥段|橋段|过门|過門|间奏|間奏|前奏|尾奏|结尾|結尾)(?:\s*([0-9一二三四五六七八九十]+))?$/i);
  if (!match) return '';
  return formatSectionTitle(match[1], match[2]);
}

function formatSectionTitle(kind, number) {
  const key = kind.toLowerCase().replace(/\s+/g, '-');
  const names = {
    verse: 'Verse', chorus: 'Chorus', bridge: 'Bridge', outro: 'Outro',
    intro: 'Intro', hook: 'Hook', refrain: 'Refrain', interlude: 'Interlude',
    rap: 'Rap', prelude: 'Prelude', 'pre-chorus': 'Pre-Chorus',
    prechorus: 'Pre-Chorus', 'post-chorus': 'Post-Chorus',
    postchorus: 'Post-Chorus', 主歌: 'Verse', 副歌: 'Chorus',
    桥段: 'Bridge', 橋段: 'Bridge', 过门: 'Interlude', 過門: 'Interlude',
    间奏: 'Interlude', 間奏: 'Interlude', 前奏: 'Intro',
    尾奏: 'Outro', 结尾: 'Outro', 結尾: 'Outro',
  };
  return `${names[key] || kind}${number ? ` ${number}` : ''}`;
}

function normalizeSections(sections) {
  const normalized = sections.map((section) => ({ ...section, lines: [...section.lines] }));
  const first = normalized[0]?.lines?.[0] || '';
  const title = extractSongTitle(first);
  if (title) normalized[0].lines.shift();
  return { title, normalized: normalized.filter((section) => section.lines.length) };
}

function extractSongTitle(line) {
  const text = String(line || '').trim();
  const named = text.match(/^(?:歌名|title)\s*[:：]\s*(.+)$/i);
  if (named) return named[1].trim();
  if (/^《.+》$/.test(text)) return text;
  return '';
}

function withLyricIndexes(items) {
  let lyricIndex = -1;
  const indexed = items.map((item) => {
    if (item.type !== 'lyric') return item;
    lyricIndex += 1;
    return { ...item, lyricIndex };
  });
  return lyricIndex >= 0 ? indexed : [{ type: 'lyric', text: 'Music preview', lyricIndex: 0 }];
}

function lyricWeight(text) {
  const length = Array.from(String(text || '').replace(/\s+/g, '')).length;
  return Math.min(2.4, Math.max(0.8, length / 12));
}

function getTimedActiveIndex(timedLyrics, currentSeconds) {
  const currentMs = Math.max(0, currentSeconds * 1000);
  let activeIndex = -1;
  for (let index = 0; index < timedLyrics.length; index += 1) {
    const item = timedLyrics[index];
    if (currentMs >= item.startTimeMs) activeIndex = index;
    if (currentMs < item.startTimeMs) break;
  }
  return activeIndex;
}
